import { useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Button, Chip, ClickAwayListener, Divider, FormControl,
  MenuItem, MenuList, Paper, Popper, Select,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import ViewSidebarIcon    from '@mui/icons-material/ViewSidebar'
import CompareArrowsIcon  from '@mui/icons-material/CompareArrows'
import { DatePicker }     from '@mui/x-date-pickers'
import moment             from 'moment'
import { loadFilterOptions } from '../../reducers/activityReducer'
import {
  setAggLevel, setDateFrom, setDateTo,
  setOperator, setVendor, setLayer, setSiteId,
  setServer, setTac, setTech2, setNumCharts, setSiteLabelMode,
} from '../../reducers/filtersReducer'
import { applyAndFetchKpiData, fetchTech2Data } from '../../actions/kpiDataActions'
import { clearTechData }                        from '../../reducers/kpiDataReducer'
import ActivityKPIDashboardExportButton          from '../charts/ActivityKPIDashboardExportButton'
import { MultiSelectDropdown, FieldGroup, Label } from './ActivityKPIDashboardMultiSelect'

// Aggregation dimensions (multi-select). No 'Activity' entry — an empty
// selection means Activity-level (whole activity, no breakout).
const AGG_LEVELS  = ['Operator', 'Server', 'TAC', 'Vendor', 'SiteId', 'Layer']
const TECHS       = ['5G', '4G', '3G', '2G']
const TECH_COLORS = {
  '5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
  '4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  '3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
  '2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

// Filters are stored as arrays; the shared dropdown speaks comma-joined strings.
const fromCSV = (csv) => (csv ? csv.split(',') : [])

export default function ActivityKPIDashboardFilterBar({ onOpenPicker, showMarks, onToggleMarks, showLegend, onToggleLegend, gridRef, activityTitle }) {
  const dispatch   = useDispatch()
  const selected   = useSelector(s => s.activity.selected)
  const opts       = useSelector(s => s.activity.filterOptions)
  const filters    = useSelector(s => s.filters)
  const loading    = useSelector(s => s.kpiData.loading)
  const compareRef = useRef(null)
  const [techPopover, setTechPopover] = useState(false)

  const { tech1, tech2, numCharts } = filters
  const comparing      = tech2 !== null
  const availableTechs = TECHS.filter(t => t !== tech1)

  // Site facet granularity follows the "Site as" toggle:
  //  • csr  → one row per CSR, value = CSR (SPs group/filter the SiteId level by CSR)
  //  • jvid → one row per JV_ID, value = JV_ID (each JV_ID treated as a distinct site)
  // `search` carries both tokens so either finds the row; rows are sorted by label.
  const siteLabelMode = filters.siteLabelMode ?? 'csr'
  const siteOptions = useMemo(() => {
    const rows = []
    for (const o of (opts?.sites ?? [])) {
      const jvids = o.jvids ?? []
      if (siteLabelMode === 'jvid' && jvids.length) {
        for (const jv of jvids) rows.push({ key: jv, value: jv, label: jv, search: `${jv} ${o.csr}` })
      } else {
        rows.push({ key: o.csr, value: o.csr, label: o.csr, search: `${o.csr} ${jvids.join(' ')}` })
      }
    }
    rows.sort((a, b) => a.label.localeCompare(b.label))
    return rows
  }, [opts?.sites, siteLabelMode])

  const toCSV = (v) => {
    if (!v) return null
    const arr = Array.isArray(v) ? v : [v]
    return arr.length > 0 ? arr.join(',') : null
  }

  const refreshCascade = (patch) => {
    const merged = { ...filters, ...patch }
    dispatch(loadFilterOptions({
      id: selected.activityId,
      filters: {
        operator: toCSV(merged.operator),
        vendor:   toCSV(merged.vendor),
        layer:    toCSV(merged.layer),
        siteId:   toCSV(merged.siteId),
        server:   toCSV(merged.server),
        tac:      toCSV(merged.tac),
        siteBy:   merged.siteLabelMode === 'jvid' ? 'Jvid' : 'Csr',
      },
    }))
  }

  const handleToggleCompare = () => setTechPopover(v => !v)

  const handleRemoveCompare = () => {
    dispatch(clearTechData(tech2))
    dispatch(setTech2(null))
    dispatch(applyAndFetchKpiData())
    setTechPopover(false)
  }

  const handleSelectTech2 = (tech) => {
    setTechPopover(false)
    if (tech2 && tech2 !== tech) dispatch(clearTechData(tech2))
    dispatch(setTech2(tech))
    dispatch(fetchTech2Data(tech))
  }

  const btnSx = (active) => ({
    height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
    border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
    borderRadius: '6px !important', gap: 0.5,
    '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.main' },
  })

  return (
    <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 2.5, pt: 1, pb: 0.75 }}>

        {/* ══ ROW 1: Activity · Date · View · Options ═══════════════════════ */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>

          {/* GROUP: Activity */}
          <FieldGroup title="Activity">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>&nbsp;</Label>
              <Chip
                label={selected?.title ?? 'None selected'}
                onClick={onOpenPicker}
                deleteIcon={<Typography sx={{ fontSize: 10, mr: 0.5 }}>✎</Typography>}
                onDelete={onOpenPicker}
                size="small"
                sx={{
                  height: 28, borderRadius: 20, bgcolor: 'primary.light',
                  border: '1px solid', borderColor: 'primary.main',
                  color: 'primary.dark', fontWeight: 500, fontSize: 11,
                  maxWidth: 300, cursor: 'pointer',
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </Box>
          </FieldGroup>

          {/* GROUP: Date */}
          <FieldGroup title="Date">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>First Day</Label>
              <DatePicker
                format="YYYY-MM-DD"
                value={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : null}
                onChange={v => dispatch(setDateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                maxDate={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
                slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>Last Day</Label>
              <DatePicker
                format="YYYY-MM-DD"
                value={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD') : null}
                onChange={v => dispatch(setDateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                minDate={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
                slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
              />
            </Box>
          </FieldGroup>

          {/* GROUP: View */}
          <FieldGroup title="View">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>&nbsp;</Label>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>

                <ToggleButton value="marks" selected={showMarks} onChange={onToggleMarks} size="small" sx={btnSx(showMarks)}>
                  <BookmarkBorderIcon sx={{ fontSize: 13 }} />
                  Marks
                </ToggleButton>

                <ToggleButton value="legend" selected={showLegend} onChange={onToggleLegend} size="small" sx={btnSx(showLegend)}>
                  <ViewSidebarIcon sx={{ fontSize: 13 }} />
                  Legend
                </ToggleButton>

                <Button ref={compareRef} onClick={handleToggleCompare} size="small"
                  variant={comparing ? 'contained' : 'outlined'}
                  sx={{
                    height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                    gap: 0.5, minWidth: 0,
                    ...(comparing ? {
                      bgcolor: 'primary.main', color: '#fff',
                      '&:hover': { bgcolor: 'primary.dark' },
                    } : {
                      borderColor: 'divider', color: 'text.secondary',
                      '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    })
                  }}>
                  <CompareArrowsIcon sx={{ fontSize: 13 }} />
                  {comparing && tech2 ? tech2 : 'Compare'}
                </Button>

                <Popper open={techPopover} anchorEl={compareRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
                  <ClickAwayListener onClickAway={() => setTechPopover(false)}>
                    <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 130 }}>
                      <Box sx={{ px: 1.5, py: '6px', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Compare with
                        </Typography>
                      </Box>
                      <MenuList dense>
                        {availableTechs.map(t => {
                          const c = TECH_COLORS[t]
                          return (
                            <MenuItem key={t} onClick={() => handleSelectTech2(t)} selected={tech2 === t}
                              sx={{ gap: 1, '&.Mui-selected': { bgcolor: 'primary.light' } }}>
                              <Chip label={t} size="small"
                                sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1 }} />
                            </MenuItem>
                          )
                        })}
                        {comparing && (
                          <MenuItem onClick={handleRemoveCompare}
                            sx={{ gap: 1, borderTop: '1px solid', borderColor: 'divider', color: 'error.main' }}>
                            <Typography sx={{ fontSize: 11 }}>✕ Remove Compare</Typography>
                          </MenuItem>
                        )}
                      </MenuList>
                    </Paper>
                  </ClickAwayListener>
                </Popper>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 20, alignSelf: 'center' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 500 }}>Charts</Typography>
                  <FormControl size="small">
                    <Select value={numCharts} onChange={e => dispatch(setNumCharts(Number(e.target.value)))}
                      sx={{ height: 28, fontSize: 11, minWidth: 52, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                      {[1, 2, 3].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 11 }}>{n}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>

              </Box>
            </Box>
          </FieldGroup>

          {/* GROUP: Options — Export */}
          <FieldGroup title="Options">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>&nbsp;</Label>
              <ActivityKPIDashboardExportButton containerRef={gridRef} title={activityTitle ?? 'Activity_KPI_Dashboard'} />
            </Box>
          </FieldGroup>

          <Box sx={{ flex: 1 }} />
        </Box>

        {/* ══ ROW 2: Filters · Aggregation · Apply ══════════════════════════ */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap',
          pt: 0.75, borderTop: '1px dashed', borderColor: 'divider',
        }}>

          {/* GROUP: Filters — cascade multiselects */}
          <FieldGroup title="Filters">
            <MultiSelectDropdown label="Operator" value={filters.operator} options={opts?.operators ?? []}
              onChange={csv => { const v = fromCSV(csv); dispatch(setOperator(v)); refreshCascade({ operator: v }) }}
              disabled={!selected} />
            <MultiSelectDropdown label="Server" value={filters.server} options={opts?.servers ?? []}
              onChange={csv => { const v = fromCSV(csv); dispatch(setServer(v)); refreshCascade({ server: v }) }}
              disabled={!selected} />
            <MultiSelectDropdown label="TAC/LAC" value={filters.tac} options={opts?.tacs ?? []}
              onChange={csv => { const v = fromCSV(csv); dispatch(setTac(v)); refreshCascade({ tac: v }) }}
              disabled={!selected} />
            <MultiSelectDropdown label="Vendor" value={filters.vendor} options={opts?.vendors ?? []}
              onChange={csv => { const v = fromCSV(csv); dispatch(setVendor(v)); refreshCascade({ vendor: v }) }}
              disabled={!selected} />
            <MultiSelectDropdown label="Layer" value={filters.layer} options={opts?.layers ?? []}
              onChange={csv => { const v = fromCSV(csv); dispatch(setLayer(v)); refreshCascade({ layer: v }) }}
              disabled={!selected} />
            <MultiSelectDropdown label="Site" value={filters.siteId} options={siteOptions}
              onChange={csv => { const v = fromCSV(csv); dispatch(setSiteId(v)); refreshCascade({ siteId: v }) }}
              disabled={!selected} />
            {/* Display preference — show sites as their CSR or their JVID (in the
                filter above AND the legends). Value sent to the queries is always
                the CSR, so this is purely cosmetic. */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>Site as</Label>
              <ToggleButtonGroup value={siteLabelMode} exclusive size="small"
                onChange={(_, v) => { if (v) { dispatch(setSiteLabelMode(v)); refreshCascade({ siteId: [], siteLabelMode: v }) } }} sx={{ height: 28 }}>
                <ToggleButton value="csr" sx={{
                  px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                }}>CSR</ToggleButton>
                <ToggleButton value="jvid" sx={{
                  px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                }}>JVID</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </FieldGroup>

          {/* GROUP: Aggregation — multi-select. Pick several dimensions to break
              series out by more than one field (e.g. Server + TAC). Empty =
              Activity-level. */}
          <FieldGroup title="Aggregation">
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Label>&nbsp;</Label>
              <MultiSelectDropdown value={filters.aggLevel} options={AGG_LEVELS} emptyLabel="Activity"
                onChange={csv => dispatch(setAggLevel(csv))} />
            </Box>
          </FieldGroup>

          {/* Apply sits right after Aggregation */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button variant="contained" size="small" disabled={!selected || loading}
              onClick={() => { refreshCascade({}); dispatch(applyAndFetchKpiData()) }}
              sx={{ height: 30, px: 3, fontWeight: 600 }}>
              Apply
            </Button>
          </Box>

        </Box>
      </Box>
    </Paper>
  )
}
